import {
  Button,
  Accordion,
  AccordionHeader,
  AccordionBody,
} from "@material-tailwind/react";
import { Fragment, useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { checkApi } from "../../Authentication/AxiosInstance";
import LoadingPage from "../common/LoadingPage";
import ErrorPage from "../common/ErrorPage";
import { groupByAgentName } from "./utils"; // Import the grouping function # actually by url & agent
import DisclaimerBanner from "../common/Disclaimers";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
export const CapsNotices = () => {
  const [open, setOpen] = useState(1);
  const [hiddenAgents, setHiddenAgents] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [groupedData, setGroupedData] = useState();
  const [notices, setNotices] = useState([]);
  const [lastImportDates, setLastImportDates] = useState({});
  const [startIndex, setStartIndex] = useState(0);
  const noticesPerPage = 6;
  const noticesRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(0);

  const formatDate = (datetimeString) => {
    const dateObj = new Date(datetimeString);
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();

    const formattedDate = dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

    if (hours === 0 && minutes === 0) {
        return formattedDate;
    }

    const formattedTime = dateObj.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });

    return `${formattedDate}, ${formattedTime}`; // Non-breaking space after comma
  };

  const fetchCapNotices = async () => {
    try {
      const fetchUrl = new URL(`${API_ENDPOINT}/capzone/capsnotices/`);
      const res = await checkApi.get(fetchUrl);
      const contentType = res.headers["content-type"];
      if (!contentType || !contentType.includes("application/json")) {
        console.error("❌ Not JSON:", res.data);
        throw new Error("Expected JSON, got something else.");
      }
      return res.data;
    } catch (error) {
      if (error.response) {
        console.error("Server error response:", error.response.data);
        throw new Error(`HTTP ${error.response.status}`);
      } else {
        console.error("Network or other error:", error.message);
        throw error;
      }
    }
    
  };

  const { data, status } = useQuery({
    queryKey: ["capnotices"],
    queryFn: fetchCapNotices,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    throwOnError: true,
  });

  const buttonCSS =  "m-1 sm:m-2 p-1.5 sm:p-2 md:p-2 normal-case text-xs sm:text-sm md:text-base text-primary-900 font-semibold tracking-wider shadow-none rounded-sm bg-white pl-2 sm:pl-3 pr-2 sm:pr-3 pt-1 pb-1 hover:bg-primary-900 hover:text-white hover:shadow-md hover:rounded-md transition-all duration-200"
  const contentCSS = `m-1 sm:m-2 p-1.5 sm:p-2 md:p-2.5 h-auto w-fit 
                      bg-transparent text-primary-900 hover:text-white hover:bg-primary-900 
                      border-2 rounded-lg 
                      text-xs sm:text-sm md:text-base font-bold tracking-wide sm:tracking-widest normal-case items-center transition-all duration-200`;

  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  }
  const fetchNoticePDF = async(noticeId) => {
    try {
        const res = await checkApi.get(`${API_ENDPOINT}/capzone/capsnotices/${noticeId}/pdf/`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data)
        if (window.innerWidth < 768) {
          // On mobile: open in new tab
          window.open(url, "_blank");
        } else {
          // On desktop: show inline iframe
          setPdfUrl(url);
        }
      } catch (error) {
          console.error('Error Loading PDF', error)
      }
  }

  const toggleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const removeAgentFromView = (agent) => {
    // Close the accordion if it's open
    if (open === agent) {
      setOpen(0);
    }
    // Remove the agent from view
    setHiddenAgents((prevHiddenAgents) => {
      if (!prevHiddenAgents.includes(agent)) {
        return [...prevHiddenAgents, agent];
      }
      return prevHiddenAgents;
    });
    // Clear PDF if it was open
    setPdfUrl(null);
    // Reset pagination
    setStartIndex(0);
  };

  const handleAgentHeaderClick = (agent) => {
    setOpen((open)=>(open === agent ? 0 : agent))
    setPdfUrl(null)
    setStartIndex(0)
  };

  const showAllAgents = () => {
    setHiddenAgents([]);
  };

  const handlePrevious = (agent, totalNotices) => {
    setStartIndex((prev) => Math.max(prev - noticesPerPage, 0));
  };

  const handleNext = (agent, totalNotices) => {
    setStartIndex((prev) => Math.min(prev + noticesPerPage, totalNotices - (totalNotices % noticesPerPage || noticesPerPage)));
  };
  
  const containsLinkWords = (text) => {
    return /\b(Click|Link)\b/i.test(text);
  };

  useEffect(() => {
    if (data) {
      setNotices(data.notices);
      setLastImportDates(data.last_import_dates);
    }
  },[data]);

  useEffect(() => {
    if (notices && notices.length) {
      const grouped = groupByAgentName(notices,'caps');
      setGroupedData(grouped);
    }
  },[notices]);

  useEffect(() => {
    if (noticesRef.current) {
      setIframeHeight(noticesRef.current.offsetHeight);
    }
  }, [noticesRef.current?.offsetHeight]); // rerun if height changes
  console.log(status);
  console.log(notices);
  console.log(lastImportDates);
  return (
    <Fragment>
      {status === 'loading' && <LoadingPage />}
      {status === 'error' && <ErrorPage />}
      {status === 'success' && (
        <div>
          <div className="flex flex-col lg:flex-row w-full p-2 sm:p-4 md:p-5 gap-2 sm:gap-4 md:gap-5 overflow-x-hidden">
            <div className={`w-full ${fullScreen ? 'hidden' : 'lg:w-1/2'} overflow-y-auto`}>
              
              <div className="items-center">
                {/* Important Announcements and view all Notices section start*/}
                <div className="flex flex-col sm:flex-row pt-2 sm:pt-4 justify-between items-start sm:items-center w-full gap-2 sm:gap-0">
                  <h1 className='text-base sm:text-lg md:text-xl text-secondary-900 font-bold'> Important Announcements</h1>
                  <Button onClick={showAllAgents} className={`${buttonCSS} flex`}>View All Notices</Button>
                </div>
                {/* Important Announcements and view all Notices section complete*/}

                {/* LHS Content Display Part section start*/}
                <div ref={noticesRef} className="pl-1 sm:pl-2 pr-1 sm:pr-2 border-2 border-primary-500 mt-2 sm:mt-4">
                  { groupedData && Object.entries(groupedData).map(([agent, notices], index) => {
                    const dashIndex = agent.indexOf('-');
                    const agentName = agent.substring(0, dashIndex);
                    const agentWebsite = agent.substring(dashIndex + 1);
                    console.log(agent)
                    console.log(agentName)
                    console.log(agentWebsite)
                    const visibleNotices = notices.slice(startIndex, startIndex + noticesPerPage)
                    
                    return (
                      <div key={agent} className="m-1 sm:m-2 h-auto">
                        <Accordion className="mt-0 mb-0 pr-1 sm:pr-2 mr-1 sm:mr-2" open={open === agent} 
                                  icon={<Icon id={agent} open={open} />}> 

                          {/* Header with Agent Part section start*/}
                          {!hiddenAgents.includes(agent) && (
                            <div key={agent} >
                              <AccordionHeader 
                                className="shadow-md shadow-primary-200 mr-1 sm:mr-2 h-auto min-h-[5rem] sm:min-h-[5.5rem]" 
                                onClick={() => handleAgentHeaderClick(agent)}>
                                <div className="w-full p-1 sm:p-0">
                                    <div className="flex flex-col w-full gap-1">
                                      <div className="flex gap-x-1 sm:gap-x-2 items-center flex-wrap">
                                        <label className="font-semibold text-sm sm:text-base md:text-lg mt-1 mb-1 text-primary-900 tracking-wide break-words">{agentName}</label>
                                        <span className= "p-1 sm:p-0 m-0 bg-white-200 text-sm sm:text-base shadow-none cursor-pointer touch-manipulation"
                                                  onClick={(e) => { e.stopPropagation(); removeAgentFromView(agent); }}
                                                  onKeyDown={(e)=> {
                                                    if (e.key === 'Enter'|| e.key === " ") {
                                                        removeAgentFromView(agent);
                                                    }
                                                  }}>
                                                  <i className="fas fa-trash text-gray-500 text-sm sm:text-base hover:text-red-500"></i>
                                        </span>
                                      </div>
                                      <div className="text-xs sm:text-sm">
                                        <div className="flex flex-wrap">
                                            <span className="whitespace-nowrap">website: &nbsp;</span>
                                            <a href={agentWebsite} target="_blank" rel="noopener noreferrer" 
                                                  className="text-blue-500 hover:underline break-all">
                                                  {agentWebsite}
                                            </a>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex text-xs sm:text-sm font-normal mt-1">
                                        {console.log(lastImportDates[agentName])}
                                        {console.log(agentName)}
                                        Updated On: {formatDate(lastImportDates[agentName])}
                                      </div>
                                </div>
                              </AccordionHeader>
                            </div>
                          )}
                          {/* Header with Agent Part section complete*/}
                          <div
                                className={`overflow-hidden transition-max-height duration-500 ease-in-out ${
                                  (open === agent) ? 'max-h-200' : 'max-h-0'
                                }`} >

                                {visibleNotices?.map((notice, noticeIndex) => {
                                  console.log(notice)
                                  const hasLinkRef = containsLinkWords(notice.e001_noticetitle);
                                  return (
                                  <AccordionBody key={noticeIndex} className="border-b-[1px] border-b-secondary-900 focus:outline-none py-2 sm:py-3">
                                    <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-x-4 text-xs sm:text-sm md:text-base font-normal focus:outline-none">
                                        <div className="focus:outline-none min-w-[80px] md:min-w-[100px] whitespace-nowrap text-xs sm:text-sm">
                                            {(() => {
                                                const formatted = formatDate(notice.c001_notice_issued);
                                                if (formatted.startsWith("01 Jan 1970")) return null;
                                                return formatted.split(", ").map((part, index) => (
                                                  <span key={index}>
                                                    {part}
                                                    {index === 0 && <br />} {/* Add line break after the first part (date) */}
                                                  </span>
                                                ));
                                            })()}
                                        </div>
                                    <div className="flex-grow w-full md:w-auto">
                                        <div className="flex flex-col gap-1 md:gap-0">
                                          <div className="flex flex-col md:flex-row gap-1 md:gap-2">
                                              {notice.c001_course_title &&
                                                  <div className="flex focus:outline-none m-0 sm:m-1 text-xs sm:text-sm">
                                                        {notice.c001_course_title} 
                                                  </div>
                                              }
                                              {notice.c001_courseurl &&
                                                <div className="flex focus:outline-none m-0 sm:m-1">
                                                    <a  href = {notice.c001_course_url} 
                                                        target="_blank" rel="noopener noreferrer"
                                                        className="underline text-secondary-900 hover:text-secondary-500 visited:text-purple-600 text-xs sm:text-sm break-all md:break-normal"
                                                    >{notice.c001_course_url}</a>
                                                </div>
                                              }
                                          </div>
                                          <div className="flex flex-col md:flex-row gap-2 md:gap-0 items-start md:items-center">
                                            {notice.c001_notice_title &&
                                              <div className="focus:outline-none m-0 sm:m-1 flex-1">    
                                                  <div title={ hasLinkRef 
                                                       ? 'This notice refers to clickable links. Please visit the official website to access them.'
                                                       : ''
                                                       }
                                                       className="text-xs sm:text-sm md:text-base break-words"
                                                  >
                                                    {/* if the notice has clickable links, show two stars and hover message to visit official site*/}
                                                    {hasLinkRef && <strong>**</strong>} 
                                                    {notice.c001_notice_title} 
                                                  </div>
                                              </div>
                                            }
                                            {notice.c001_haspdf_c && 
                                              <div className="focus:outline-none mt-1 md:mt-0">
                                                  <Button className={`${contentCSS}`}
                                                  onClick={()=> fetchNoticePDF(notice.id)}>View Notice</Button>
                                              </div>
                                            }
                                          </div>
                                          
                                      </div>
                                    </div>
                                    </div>
                                  </AccordionBody>
                                
                              )})}
                          </div>
                          {/* Limit data to 6 rows and show View More Button */}
                          {(open === agent) && notices.length > 6 &&
                            <div  className="flex flex-row justify-between align-center gap-2 sm:gap-4 mt-2 sm:mt-4 mb-2 sm:mb-4"
                                    onMouseDown={(e) => e.preventDefault()}> 
                              <Button className = {`${contentCSS} flex `}
                                      disabled={startIndex === 0} 
                                      onClick={() => handlePrevious(agent)}>
                                      Previous
                              </Button>
                              <Button className = {`${contentCSS} flex `}
                                      disabled={startIndex + noticesPerPage >= notices.length} 
                                      onClick={() => handleNext(agent, notices.length)}>
                                      Next
                              </Button>
                            </div>
                          }
                        </Accordion>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            {/* LHS Content Display Part section complete*/}
            {/* RHS PDF Display Part section start*/}
            {pdfUrl && (
              <div className={`hidden lg:block ${fullScreen ? 'w-full' : 'lg:w-1/2'} h-full overflow-y-auto mr-2 sm:mr-4 md:mr-5 border-[1px]`}>
                <div className="flex flex-row mt-2 sm:mt-4 p-2 sm:p-0">
                  <div className="flex ml-0 mr-auto justify-start text-secondary-900 font-bold"></div>
                  <div className="flex ml-auto mr-0 justify-end">
                        <Button
                            onClick={toggleFullScreen}
                            className={`${buttonCSS}`}
                          >
                            {fullScreen ? 'Exit Full Screen' : 'Full Screen'}
                        </Button>
                  </div>
                </div>
                <div className="h-auto overflow-y-auto p-1 sm:p-0">
                      <iframe src={pdfUrl} style={{ minHeight: `${iframeHeight}px` }} className="w-full border-0" title="PDF Viewer" /> 
                </div> 
              </div>
            )}
            {/* RHS PDF Display Part section complete*/}
          </div>
          <DisclaimerBanner/>
        </div>
      )}
    </Fragment>
  );
};

